// Product form behavior: variant flags (coming soon / waitlist / sold out),
// "Only A Few Left" display, popout selector cleanup, price guard, and
// change-event loop prevention. Extracted from inline <script> in product-form.liquid.
// Handles multiple form instances (PDP, upsell modal, quick-add) via querySelectorAll.

declare global {
  interface Window {
    __bbChangeGuardInstalled?: boolean;
  }
  interface HTMLElement {
    __bbChanging?: boolean;
  }
}

interface VariantFlags {
  comingSoon: boolean;
  joinWaitlist: boolean;
}

const SUFFIX_RE = /\s*-\s*Please select your size\s*$/i;

function cleanText(s: string | null | undefined): string {
  return String(s || "").replace(SUFFIX_RE, "").trim();
}

function parseJsonIsland(id: string): Record<string, unknown> {
  try {
    const el = document.getElementById(id);
    if (el) return JSON.parse(el.textContent || "{}");
  } catch {}
  return {};
}

class ProductFormInstance {
  private form: HTMLFormElement;
  private productId: string;
  private submitBlock: HTMLElement | null;
  private comingSoonBlock: HTMLElement | null;
  private fewLeftEl: HTMLElement | null;
  private variantIdInput: HTMLInputElement | null;
  private submitBtn: HTMLButtonElement | null;
  private btnText: HTMLElement | null;
  private flagsMap: Record<string, VariantFlags>;
  private fewLeftMap: Record<string, boolean>;
  private popouts: HTMLElement[];

  // Translated strings from data attributes
  private textJoinWaitlist: string;
  private textSoldOut: string;
  private textAddToCart: string;

  constructor(form: HTMLFormElement) {
    this.form = form;
    this.productId = form.dataset.productId || "";

    this.submitBlock = form.querySelector("[data-submit-block]");
    this.comingSoonBlock = form.querySelector("[data-coming-soon-block]");
    this.fewLeftEl = form.querySelector("[data-few-left]");
    this.variantIdInput = form.querySelector('input[name="id"]');
    this.submitBtn = form.querySelector("[data-add-to-cart]");
    this.btnText =
      this.submitBtn?.querySelector("[data-add-to-cart-text]") || this.submitBtn;

    this.flagsMap = parseJsonIsland(
      `variant-flags-map-${this.productId}`
    ) as Record<string, VariantFlags>;
    this.fewLeftMap = parseJsonIsland(
      `few-left-map-${this.productId}`
    ) as Record<string, boolean>;

    this.textJoinWaitlist = form.dataset.textJoinWaitlist || "";
    this.textSoldOut = form.dataset.textSoldOut || "";
    this.textAddToCart = form.dataset.textAddToCart || "";

    this.popouts = Array.from(form.querySelectorAll<HTMLElement>(".select-popout"));
  }

  init() {
    this.initPopoutSelectors();

    this.applyVariantFlags();
    this.updateFewLeft();
    this.popouts.forEach((p) => this.cleanSelect(p));

    if (this.variantIdInput) {
      this.variantIdInput.addEventListener("change", () => {
        setTimeout(() => {
          this.applyVariantFlags();
          this.updateFewLeft();
          this.popouts.forEach((p) => this.cleanSelect(p));
        }, 0);
      });
    }

    const colorSelect = this.form.querySelector<HTMLElement>(
      "[data-color-fallback-select]"
    );
    if (colorSelect) {
      colorSelect.addEventListener("change", () => {
        setTimeout(() => {
          this.applyVariantFlags();
          this.updateFewLeft();
          this.popouts.forEach((p) => this.cleanSelect(p));
        }, 50);
      });
    }

    this.initPriceGuard();
  }

  private toggleNotifyByCartState() {
    if (!this.submitBtn || !this.btnText) return;
    const allowWaitlist = JSON.parse(
      this.form.getAttribute("data-join-waitlist") || "false"
    );
    const isDisabled =
      this.submitBtn.disabled ||
      this.submitBtn.classList.contains("variant-soldout");
    this.btnText.textContent = isDisabled
      ? allowWaitlist
        ? this.textJoinWaitlist
        : this.textSoldOut
      : this.textAddToCart;
  }

  private applyVariantFlags() {
    const vid = this.variantIdInput?.value;
    const flags: VariantFlags = (vid && this.flagsMap[vid]) || {
      comingSoon: false,
      joinWaitlist: false,
    };
    if (this.comingSoonBlock)
      this.comingSoonBlock.style.display = flags.comingSoon ? "" : "none";
    if (this.submitBlock)
      this.submitBlock.style.display = flags.comingSoon ? "none" : "";
    this.form.setAttribute(
      "data-join-waitlist",
      JSON.stringify(!!flags.joinWaitlist)
    );
    this.toggleNotifyByCartState();
  }

  private updateFewLeft() {
    const vid = this.variantIdInput?.value;
    const show = !!(vid && this.fewLeftMap[vid]);
    if (this.fewLeftEl) this.fewLeftEl.style.display = show ? "block" : "none";
  }

  private cleanSelect(select: HTMLElement) {
    select.querySelectorAll("[data-popout-option] span").forEach((span) => {
      const t = span.textContent || "";
      const nt = cleanText(t);
      if (nt !== t) span.textContent = nt;
    });
    const label = select.querySelector<HTMLElement>("[data-selected-label]");
    const input = select.querySelector<HTMLInputElement>(
      "input[data-popout-input]"
    );
    if (label && input && input.value) label.textContent = cleanText(input.value);
  }

  private initPopoutSelectors() {
    for (const select of this.popouts) {
      const options =
        select.querySelectorAll<HTMLElement>("[data-popout-option]");
      const label = select.querySelector<HTMLElement>("[data-selected-label]");
      const input = select.querySelector<HTMLInputElement>(
        "input[data-popout-input]"
      );
      const toggle = select.querySelector<HTMLElement>("[data-popout-toggle]");

      this.cleanSelect(select);
      if (toggle)
        toggle.addEventListener("click", () =>
          setTimeout(() => this.cleanSelect(select), 0)
        );

      options.forEach((option) => {
        option.addEventListener("click", () => {
          const value = option.getAttribute("data-value") || "";
          if (!value || !input) return;

          options.forEach((o) => o.removeAttribute("aria-current"));
          option.setAttribute("aria-current", "true");

          input.value = value;
          input.dispatchEvent(new Event("input", { bubbles: true }));

          if (label) label.textContent = cleanText(value);

          this.submitBtn?.classList.remove("btn--error");
          setTimeout(() => {
            this.toggleNotifyByCartState();
            this.applyVariantFlags();
            this.updateFewLeft();
          }, 50);
        });
      });
    }
  }

  // Fallback: if the submit button errors out and price shows $0.00,
  // restore the default price until the error clears.
  private initPriceGuard() {
    const defaultPrice = this.form.dataset.defaultPrice;
    if (!defaultPrice) return;

    const priceEl = document.querySelector<HTMLElement>("[data-product-price]");
    const check = setInterval(() => {
      if (!this.submitBtn || !priceEl) return;
      const isError =
        this.submitBtn.classList.contains("btn--error") ||
        this.submitBtn.disabled;
      if (isError && priceEl.textContent?.trim() === "$0.00")
        priceEl.textContent = defaultPrice;
      if (!isError) clearInterval(check);
    }, 100);
  }
}

// Prevents synchronous re-entrant `change` loops on variant selectors.
// Global and run-once — safe across multiple form instances.
function installChangeGuard() {
  if (window.__bbChangeGuardInstalled) return;
  window.__bbChangeGuardInstalled = true;

  document.addEventListener(
    "change",
    (e) => {
      const el = e.target as HTMLElement | null;
      if (
        !el ||
        !el.matches(
          "[data-single-option-selector], [data-color-fallback-select], [data-bb-fallback-select-1], [data-bb-fallback-select-2]"
        )
      )
        return;

      if (el.__bbChanging) {
        e.stopImmediatePropagation();
        return;
      }
      el.__bbChanging = true;
      setTimeout(() => {
        el.__bbChanging = false;
      }, 0);
    },
    true
  );
}

function initForms() {
  // Only target forms with data-product-id — the opt-in signal that this form
  // provides the data-* attributes we need. product-form-old.liquid doesn't have
  // these and retains its own inline script.
  document
    .querySelectorAll<HTMLFormElement>(
      'form[data-product-form][data-product-id]:not([data-pf-initialized])'
    )
    .forEach((form) => {
      form.setAttribute("data-pf-initialized", "");
      new ProductFormInstance(form).init();
    });
}

export const productForm = {
  init: () => {
    installChangeGuard();
    initForms();

    // Re-init after Shopify section rendering (theme customizer, AJAX section loads)
    document.addEventListener("shopify:section:load", () => initForms());
  },
};
